import { useEffect, useRef } from 'react';
import { applyStyle } from 'ol-mapbox-style';
import { layers as pmLayers, namedFlavor } from '@protomaps/basemaps';
import VectorTileLayer from 'ol/layer/VectorTile';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { PMTilesVectorSource } from 'ol-pmtiles';

// Custom light flavor — white land, blue-grey water, faint landcover
const customLightFlavor = {
  ...namedFlavor('light'),
  // Water/ocean
  background: '#c5d8e2',
  water: '#c5d8e2',

  // Land: semi-transparent white so hillshade shows through
  earth: 'rgba(250, 250, 250, 0.85)',

  // Landcover: semi-transparent so hillshade shows through
  landcover: {
    grassland: 'rgba(240, 245, 238, 0.3)',
    barren: 'rgba(245, 243, 240, 0.2)',
    urban_area: 'rgba(245, 245, 245, 0.2)',
    farmland: 'rgba(242, 247, 240, 0.2)',
    glacier: 'rgba(252, 252, 255, 0.3)',
    scrub: 'rgba(242, 247, 240, 0.2)',
    forest: 'rgba(238, 245, 238, 0.3)',
  },

  // Parks: semi-transparent
  park_a: 'rgba(242, 247, 243, 0.5)',
  park_b: 'rgba(234, 242, 235, 0.5)',

  // Landuse: semi-transparent
  hospital: 'rgba(250, 248, 248, 0.5)',
  industrial: 'rgba(246, 248, 249, 0.5)',
  school: 'rgba(250, 248, 246, 0.5)',
  wood_a: 'rgba(240, 245, 240, 0.4)',
  wood_b: 'rgba(232, 240, 232, 0.4)',
  pedestrian: 'rgba(248, 247, 245, 0.5)',
  scrub_a: 'rgba(242, 246, 244, 0.4)',
  scrub_b: 'rgba(232, 240, 236, 0.4)',
  glacier: 'rgba(250, 250, 250, 0.4)',
  sand: 'rgba(248, 246, 244, 0.4)',
  beach: 'rgba(250, 248, 244, 0.4)',
  aerodrome: 'rgba(244, 244, 246, 0.5)',
  runway: 'rgba(246, 246, 248, 0.5)',
  zoo: 'rgba(242, 246, 246, 0.5)',
  military: 'rgba(244, 244, 244, 0.5)',

  // Buildings: very faint
  buildings: '#ececec',

  // Roads: subtle
  minor_service_casing: '#ececee',
  minor_casing: '#ececee',
  link_casing: '#ececee',
  major_casing_late: '#e8e8ec',
  highway_casing_late: '#e4e4e8',
  other: '#f6f6f8',
  minor_service: '#f6f6f8',
  minor_a: '#f6f6f8',
  minor_b: '#ffffff',
  link: '#ffffff',
  major_casing_early: '#e8e8ec',
  major: '#ffffff',
  highway_casing_early: '#e4e4e8',
  highway: '#ffffff',
  railway: '#d0d4d6',

  // Tunnels
  tunnel_other_casing: '#f0f0f2',
  tunnel_minor_casing: '#f0f0f2',
  tunnel_link_casing: '#f0f0f2',
  tunnel_major_casing: '#f0f0f2',
  tunnel_highway_casing: '#f0f0f2',
  tunnel_other: '#ececee',
  tunnel_minor: '#ececee',
  tunnel_link: '#ececee',
  tunnel_major: '#ececee',
  tunnel_highway: '#ececee',

  // Bridges
  bridges_other_casing: '#ececee',
  bridges_minor_casing: '#ececee',
  bridges_link_casing: '#ececee',
  bridges_major_casing: '#e8e8ec',
  bridges_highway_casing: '#e4e4e8',
  bridges_other: '#f6f6f8',
  bridges_minor: '#ffffff',
  bridges_link: '#ffffff',
  bridges_major: '#fafafa',
  bridges_highway: '#ffffff',

  // Pier
  pier: '#f0f0f0',

  // Boundaries
  boundaries: '#c0c0c0',

  // Labels
  roads_label_minor: '#a0a0a4',
  roads_label_minor_halo: '#ffffff',
  roads_label_major: '#909094',
  roads_label_major_halo: '#ffffff',
  ocean_label: '#8aaccc',
  subplace_label: '#a0a0a0',
  subplace_label_halo: '#ffffff',
  city_label: '#6a6a6a',
  city_label_halo: '#ffffff',
  state_label: '#c0c0c0',
  state_label_halo: '#ffffff',
  country_label: '#b0b0b0',
};

function ProtomapsLayer({
  map,
  zIndex = 0,
  opacity = 1,
  pmtilesUrl = '/tiles/basemap.pmtiles',
  theme = 'light',
  showLabels = false,
}) {
  const layerRef = useRef(null);
  const hillshadeRef = useRef(null);
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!map || appliedRef.current) return;

    // Build the full URL for the PMTiles file
    const fullUrl = pmtilesUrl.startsWith('http')
      ? pmtilesUrl
      : `${window.location.origin}${pmtilesUrl}`;

    console.log('[ProtomapsLayer] Creating layer with PMTiles URL:', fullUrl);

    // Vector basemap at bottom
    const layer = new VectorTileLayer({
      declutter: true,
      source: new PMTilesVectorSource({
        url: fullUrl,
        attributions: ['© <a href="https://openstreetmap.org">OpenStreetMap</a>'],
      }),
    });
    layer.setZIndex(zIndex - 1);
    layer.setOpacity(opacity);
    map.addLayer(layer);

    // Hillshade ON TOP with multiply blend mode via className
    // OL uses className as the CSS class on the layer's wrapper div
    // multiply blend: white in hillshade = invisible, dark shadows = darken basemap
    const hillshadeLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}',
        crossOrigin: 'anonymous',
        maxZoom: 16,
        attributions: 'Esri',
      }),
      className: 'hillshade-layer',
      opacity: 0.5,
    });
    hillshadeLayer.setZIndex(zIndex);
    map.addLayer(hillshadeLayer);
    hillshadeRef.current = hillshadeLayer;

    // Inject CSS to apply multiply blend mode on the hillshade layer div
    if (!document.getElementById('hillshade-blend-css')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'hillshade-blend-css';
      styleEl.textContent = `
        .hillshade-layer {
          mix-blend-mode: multiply !important;
        }
      `;
      document.head.appendChild(styleEl);
    }
    layerRef.current = layer;
    appliedRef.current = true;

    // Build the style spec (only used for styling, not for source resolution)
    const styleSpec = {
      version: 8,
      glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
      sprite: `https://protomaps.github.io/basemaps-assets/sprites/v4/${theme}`,
      sources: {
        protomaps: {
          type: 'vector',
          // Dummy URL — applyStyle won't use it because updateSource: false
          url: fullUrl,
        },
      },
      layers: (() => {
        const flavor = theme === 'light' ? customLightFlavor : namedFlavor(theme);
        const allLayers = pmLayers('protomaps', flavor, { lang: 'en' });
        // Filter out unwanted layers
        const filtered = allLayers.filter(l => {
          if (l.type === 'background') return false;
          if (!showLabels && l.type === 'symbol') return false;
          // Remove default boundary layers — we replace them below
          if (l.id === 'boundaries_country' || l.id === 'boundaries') return false;
          return true;
        });

        // Add custom admin boundary layers
        // Admin-1 background (subtle grey, appears at higher zoom)
        filtered.push({
          id: 'admin-1-boundary-bg',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'boundaries',
          filter: ['>', 'kind_detail', 2],
          paint: {
            'line-color': '#d9d9d9',
            'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.8, 12, 1.5],
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 8, 0.75],
          },
        });
        // Admin-1 boundary
        filtered.push({
          id: 'admin-1-boundary',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'boundaries',
          filter: ['>', 'kind_detail', 2],
          paint: {
            'line-color': ['interpolate', ['linear'], ['zoom'], 3, '#b0b0b0', 6, '#707070'],
            'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.5, 5, 0.8, 10, 1.5],
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 2, 0, 2.5, 0.8],
            'line-dasharray': [3, 1.5],
          },
        });
        // Admin-0 background (soft grey halo)
        filtered.push({
          id: 'admin-0-boundary-bg',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'boundaries',
          filter: ['<=', 'kind_detail', 2],
          paint: {
            'line-color': '#d6d6d6',
            'line-width': ['interpolate', ['linear'], ['zoom'], 3, 2, 10, 5],
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 3, 0, 4, 0.5],
          },
        });
        // Admin-0 boundary (prominent dark blue #015f94)
        filtered.push({
          id: 'admin-0-boundary',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'boundaries',
          filter: ['<=', 'kind_detail', 2],
          paint: {
            'line-color': '#015f94',
            'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.8, 10, 2],
          },
        });

        return filtered;
      })(),
    };

    // Apply only the styling to our layer, keeping our PMTilesVectorSource intact
    applyStyle(layer, styleSpec, {
      source: 'protomaps',
      updateSource: false,
    })
      .then(() => {
        console.log('[ProtomapsLayer] Style applied successfully');
      })
      .catch((err) => {
        console.error('[ProtomapsLayer] Failed to apply style:', err);
        appliedRef.current = false;
      });

    return () => {
      if (map) {
        if (hillshadeRef.current) {
          try { map.removeLayer(hillshadeRef.current); } catch (e) { /* */ }
        }
        if (layerRef.current) {
          try { map.removeLayer(layerRef.current); } catch (e) { /* */ }
        }
      }
      hillshadeRef.current = null;
      layerRef.current = null;
      appliedRef.current = false;
    };
  }, [map, pmtilesUrl, theme, showLabels]);

  // Update opacity
  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.setOpacity(opacity);
    }
  }, [opacity]);

  // Update zIndex
  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  return null;
}

export default ProtomapsLayer;
